import React, { memo, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Title2, Headline } from '../../../components/Typography';
import {
  ORDER_REFUNDED,
  ORDER_REJECTED,
  ORDER_SUBMITTED,
  ORDER_ACCEPTED,
  ORDER_COMPLETE,
  ORDER_ON_DELIVERY,
  ORDER_READY_TO_PICKUP,
  ORDER_OUT_FOR_DELIVERY,
  ORDER_DELIVERED,
  ORDER_DROPPING_OFF,
} from '../orderStatusTypes';
import calculateOrderReadyTime from '../utils/calculateOrderReadyTime';
import { withStyles } from '@material-ui/core';
import Dialog from '../../../components/Dialog/Dialog';
import ProductPreview from './ProductPreview';
import Receipt from './Receipt';
import PaymentMethod from './PaymentMethod';
import Icon from './Icon';
import TrackButton from './TrackButton';
import CloseButton from '../../../components/CloseButton/CloseButton';
import {
  PICKUP,
  DELIVERY,
  DROPOFF,
} from '../../OrderSetting/orderSettingTypes';
import appConfig from '../../../../app-config';
import StepsSummary from './StepsSummary';
const Wrapper = styled.div`
  display: grid;
`;
const ContentWrapper = styled.div``;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.mini ? '4em' : '7em')};

  @media (max-width: 480px) {
    height: 5em;
  }
`;

const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 10px;
  align-items: center;
`;

const Title = styled(Title2)`
  color: ${(props) => props.theme.orderStatusTitleColor};
  margin-bottom: 10px;
  margin-left: 5px;
`;

const DetailsWrapper = styled.div`
  padding: 2rem 24px 50px 24px;
  display: grid;
  grid-gap: 32px;
`;

const DetailWrapper = styled.div`
  display: grid;
  grid-gap: 22px;
`;

const DetailTitle = styled(Headline)`
  font-weight: 600;
  width: max-content;

  :after {
    content: '';
    display: block;
    width: calc(100% + 2px);

    border-bottom: ${(props) => props.categoriesBarSelectedBorderColorWidth}
      solid ${(props) => props.theme.orderStatusTitleBarBorderColor};
  }
`;

const ReceiptWrapper = styled.div`
  // margin: auto;
  display: flex;
  justify-content: flex-end;
`;

const NoteWraper = styled.div`
  width: 100%;
  margin: 0 5px 20px 0;
`;

const NoteText = styled.span`
  width: 100%;
  height: 17px;
  white-space: pre-line;
  margin: 0 0 0 4px;
  font-family: Inter;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  color: var(--colors-basic-dark);
`;
const Line = styled.hr`
  width: 100%;
  color: #c1c1c1;
  margin: 0;
`;

const StatusCardWrapper = styled.div`
  padding: 32px 16px 32px 24px;
  height: 17rem;
  background: ${(props) => props.theme.orderStatusBackgroundColor};
  border-radius: 10px;
  cursor: ${(props) => !props.isForModal && 'pointer'};
  justify-content: center;
  &:hover {
    background: ${(props) =>
      !props.isForModal && props.theme.orderStatusBackgroundColorHover};
    transition: all 0.25s ease;
  }
  box-shadow: ${(props) => props.theme.orderStatusCartBoxShadow ?? 'none'};
  @media (max-width: 480px) {
    height: 12rem;
  }
`;

const OrderStatusMessage = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  font-family: ${(props) => props.font || ''};
  color: ${(props) => props.customColor || 'black'};
`;

const OrderStatusSubMessage = styled.div`
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.02px;
  text-align: center;
  font-family: ${(props) => props.font || ''};
  color: ${(props) => props.customColor || '#737a87'};
`;

const Separator = styled.div`
  width: 15%;
  height: 4px;
  border-radius: 2px;
  background-color: ${(props) =>
    props.customColor || props.theme.orderStatusIconColor};
  margin: 0px 1em 0px 1em;
`;

const styles = () => ({
  scrollPaper: {},
  paper: {
    borderRadius: '10px',
  },
});

const OrderStatusBody = ({
  orderType,
  status,
  businessName,
  orderReadyTime,
  deliveryTrackingURL,
  delay,
  index,
  displayComplete,
  statusMessage,
}) => {
  return (
    <ContentWrapper>
      {status === ORDER_DELIVERED && (
        <Content>
          <Icon className='fas fa-badge-check' />
        </Content>
      )}
      {status === ORDER_COMPLETE && displayComplete && (
        <Content>
          {appConfig.styleConfig?.orderStatusStepComplete ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStepComplete}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon className='fas fa-badge-check' />
          )}
        </Content>
      )}
      {(status === ORDER_REFUNDED || status === ORDER_REJECTED) && (
        <Content>
          {appConfig.styleConfig?.orderStatusStepRefund ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStepRefund}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon className='fas fa-exclamation-triangle' />
          )}
        </Content>
      )}
      {status === ORDER_SUBMITTED && (
        <Content>
          {appConfig.styleConfig?.orderStatusStep1ActiveIcon ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStep1ActiveIcon}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon className='fas fa-check-circle' />
          )}
          <Separator
            customColor={appConfig.styleConfig?.orderStatusHorizontalLineColor}
          />
          {appConfig.styleConfig?.orderStatusStep2InactiveIcon ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStep2InactiveIcon}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon
              className='fas fa-hat-chef'
              style={{
                color:
                  appConfig.styleConfig?.orderStatusDisableIconColor ||
                  appConfig.styleConfig?.primaryButtonDisabledColor,
              }}
            />
          )}
          <Separator
            customColor={appConfig.styleConfig?.orderStatusHorizontalLineColor}
          />
          <RenderOrderCompleteIcon orderType={orderType} status={status} />
        </Content>
      )}
      {status === ORDER_ACCEPTED && (
        <Content>
          {appConfig.styleConfig?.orderStatusStep1InactiveIcon ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStep1InactiveIcon}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon
              className='fas fa-check-circle'
              style={{
                color:
                  appConfig.styleConfig?.orderStatusDisableIconColor ||
                  appConfig.styleConfig?.primaryButtonDisabledColor,
              }}
            />
          )}
          <Separator
            customColor={appConfig.styleConfig?.orderStatusHorizontalLineColor}
          />
          {appConfig.styleConfig?.orderStatusStep2ActiveIcon ? (
            <div>
              <img
                src={appConfig.styleConfig?.orderStatusStep2ActiveIcon}
                alt='wait for confirmation'
              />
            </div>
          ) : (
            <Icon className='fas fa-hat-chef' />
          )}
          <Separator
            customColor={appConfig.styleConfig?.orderStatusHorizontalLineColor}
          />
          <RenderOrderCompleteIcon orderType={orderType} status={status} />
        </Content>
      )}
      {(status === ORDER_ON_DELIVERY ||
        status === ORDER_READY_TO_PICKUP ||
        status === ORDER_OUT_FOR_DELIVERY) &&
        !displayComplete && (
          <Content>
            {appConfig.styleConfig?.orderStatusStep1InactiveIcon ? (
              <div>
                <img
                  src={appConfig.styleConfig?.orderStatusStep1InactiveIcon}
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className='fas fa-check-circle'
                style={{
                  color:
                    appConfig.styleConfig?.orderStatusDisableIconColor ||
                    appConfig.styleConfig?.primaryButtonDisabledColor,
                }}
              />
            )}
            <Separator
              customColor={
                appConfig.styleConfig?.orderStatusHorizontalLineColor
              }
            />
            {appConfig.styleConfig?.orderStatusStep2InactiveIcon ? (
              <div>
                <img
                  src={appConfig.styleConfig?.orderStatusStep2InactiveIcon}
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className='fas fa-hat-chef'
                style={{
                  color:
                    appConfig.styleConfig?.orderStatusDisableIconColor ||
                    appConfig.styleConfig?.primaryButtonDisabledColor,
                }}
              />
            )}
            <Separator
              customColor={
                appConfig.styleConfig?.orderStatusHorizontalLineColor
              }
            />
            <RenderOrderCompleteIcon orderType={orderType} status={status} />
          </Content>
        )}
      <OrderStatusMessage
        customColor={appConfig.styleConfig.orderStatusTitleColor}
        font={appConfig.styleConfig.orderStatusMessageFont}
      >
        {status === ORDER_DELIVERED ? (
          <div>
            Your order has been delivered
            <div style={{ marginTop: '25px' }}>
              <TrackButton deliveryTrackingURL={deliveryTrackingURL} />
            </div>
          </div>
        ) : status === ORDER_SUBMITTED ? (
          <>
            Your order has been placed
            <OrderStatusSubMessage
              customColor={appConfig.styleConfig.orderStatusSubtitleColor}
              font={appConfig.styleConfig.orderStatusMessageFont}
            >
              Waiting for restaurant confirmation…
            </OrderStatusSubMessage>
          </>
        ) : status === ORDER_ACCEPTED && delay === 0 ? (
          <>
            Your order is being prepared
            <OrderStatusSubMessage
              customColor={appConfig.styleConfig.orderStatusSubtitleColor}
              font={appConfig.styleConfig.orderStatusMessageFont}
            >
              {`It will be ready by ${orderReadyTime}`}
            </OrderStatusSubMessage>
          </>
        ) : status === ORDER_ACCEPTED && delay > 0 ? (
          <>
            Your order is being prepared
            <OrderStatusSubMessage
              customColor={appConfig.styleConfig.orderStatusSubtitleColor}
              font={appConfig.styleConfig.orderStatusMessageFont}
            >
              {`It will be ready by ${orderReadyTime}. Please contact the store for more information.`}
            </OrderStatusSubMessage>
          </>
        ) : status === ORDER_READY_TO_PICKUP &&
          orderType === 'pickup' &&
          !displayComplete ? (
          <>
            Your order is ready for pickup
            <OrderStatusSubMessage
              customColor={appConfig.styleConfig.orderStatusSubtitleColor}
              font={appConfig.styleConfig.orderStatusMessageFont}
            >
              {`at ${businessName}`}
            </OrderStatusSubMessage>
          </>
        ) : (status === ORDER_ON_DELIVERY ||
            status === ORDER_OUT_FOR_DELIVERY) &&
          orderType === 'delivery' &&
          !displayComplete ? (
          <>
            {appConfig.styleConfig?.deliveryOutOnLine1 ??
              'Your order is out for delivery'}
            {deliveryTrackingURL ? (
              <div style={{ marginTop: '25px' }}>
                <TrackButton deliveryTrackingURL={deliveryTrackingURL} />
              </div>
            ) : (
              <OrderStatusSubMessage
                customColor={appConfig.styleConfig.orderStatusSubtitleColor}
                font={appConfig.styleConfig.orderStatusMessageFont}
              >
                {appConfig.styleConfig?.deliveryOutOnLine2
                  ? `${appConfig.styleConfig.deliveryOutOnLine2} ${orderReadyTime}`
                  : `It will be delivered by ${orderReadyTime}`}
              </OrderStatusSubMessage>
            )}
          </>
        ) : status === ORDER_DROPPING_OFF &&
          orderType === 'drop_off' &&
          !displayComplete ? (
          <>
            {appConfig.styleConfig?.deliveryOutOnLine1 ??
              'Bringing the order to you'}
            {deliveryTrackingURL ? (
              <div style={{ marginTop: '25px' }}>
                <TrackButton deliveryTrackingURL={deliveryTrackingURL} />
              </div>
            ) : (
              <OrderStatusSubMessage
                customColor={appConfig.styleConfig.orderStatusSubtitleColor}
                font={appConfig.styleConfig.orderStatusMessageFont}
              >
                {appConfig.styleConfig?.deliveryOutOnLine2
                  ? `${appConfig.styleConfig.deliveryOutOnLine2} ${orderReadyTime}`
                  : `It will be brought to you by ${orderReadyTime}`}
                {/* {`It will be delivered by ${orderReadyTime}`} */}
              </OrderStatusSubMessage>
            )}
          </>
        ) : status === ORDER_REFUNDED || status === ORDER_REJECTED ? (
          <div>{statusMessage}</div>
        ) : status === ORDER_COMPLETE && displayComplete ? (
          <div>Your order is complete!</div>
        ) : null}
      </OrderStatusMessage>
    </ContentWrapper>
  );
};
const RenderOrderCompleteIcon = ({ orderType, status, displayComplete }) => {
  if (orderType === PICKUP)
    return (
      <>
        {status === ORDER_READY_TO_PICKUP ? (
          <>
            {appConfig.styleConfig?.orderStatusStep3ActiveIconPickup ? (
              <div>
                <img
                  src={appConfig.styleConfig?.orderStatusStep3ActiveIconPickup}
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className={'fas fa-store'}
                style={{
                  color: displayComplete
                    ? appConfig.styleConfig?.orderStatusDisableIconColor ||
                      appConfig.styleConfig?.primaryButtonDisabledColor
                    : 'default',
                }}
              />
            )}
          </>
        ) : (
          <>
            {appConfig.styleConfig?.orderStatusStep3InactiveIconPickup ? (
              <div>
                <img
                  src={
                    appConfig.styleConfig?.orderStatusStep3InactiveIconPickup
                  }
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className='fas fa-store'
                style={{
                  color:
                    appConfig.styleConfig?.orderStatusDisableIconColor ||
                    appConfig.styleConfig?.primaryButtonDisabledColor,
                }}
              />
            )}
          </>
        )}
      </>
    );

  if (orderType === DELIVERY)
    return (
      <>
        {status === ORDER_ON_DELIVERY ||
        status === ORDER_OUT_FOR_DELIVERY ||
        status === ORDER_DELIVERED ? (
          <>
            {appConfig.styleConfig?.orderStatusStep3ActiveIconDelivery ? (
              <div>
                <img
                  src={
                    appConfig.styleConfig?.orderStatusStep3ActiveIconDelivery
                  }
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className={'fas fa-car-side'}
                style={{
                  color: displayComplete
                    ? appConfig.styleConfig?.orderStatusDisableIconColor ||
                      appConfig.styleConfig?.primaryButtonDisabledColor
                    : 'default',
                }}
              />
            )}
          </>
        ) : (
          <>
            {appConfig.styleConfig?.orderStatusStep3InactiveIconDelivery ? (
              <div>
                <img
                  src={
                    appConfig.styleConfig?.orderStatusStep3InactiveIconDelivery
                  }
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className='fas fa-car-side'
                style={{
                  color:
                    appConfig.styleConfig?.orderStatusDisableIconColor ||
                    appConfig.styleConfig?.primaryButtonDisabledColor,
                }}
              />
            )}
          </>
        )}
      </>
    );

  if (orderType === DROPOFF)
    return (
      <>
        {status === ORDER_COMPLETE ? (
          <>
            {appConfig.styleConfig?.orderStatusStep3ActiveIconDelivery ? (
              <div>
                <img
                  src={
                    appConfig.styleConfig?.orderStatusStep3ActiveIconDelivery
                  }
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className={'fas fa-shopping-bag'}
                style={{
                  color: displayComplete
                    ? appConfig.styleConfig?.orderStatusDisableIconColor ||
                      appConfig.styleConfig?.primaryButtonDisabledColor
                    : 'default',
                }}
              />
            )}
          </>
        ) : (
          <>
            {appConfig.styleConfig?.orderStatusStep3InactiveIconDelivery ? (
              <div>
                <img
                  src={
                    appConfig.styleConfig?.orderStatusStep3InactiveIconDelivery
                  }
                  alt='wait for confirmation'
                />
              </div>
            ) : (
              <Icon
                className='fas fa-shopping-bag'
                style={{
                  color:
                    appConfig.styleConfig?.orderStatusDisableIconColor ||
                    appConfig.styleConfig?.primaryButtonDisabledColor,
                }}
              />
            )}
          </>
        )}
      </>
    );
};

const OrderStatusModal2 = ({
  orderStatusModalData,
  setOrderStatusModalData,
  classes,
  orderDelayMessage,
  currency,
  is_tip_enabled,
  displayComplete,
  locations,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const updatePhoneNumber = useCallback(() => {
    if (orderStatusModalData) {
      const businessId = orderStatusModalData?.business_id;
      const selectedBusiness = locations?.find(
        (business) => business.id === businessId
      );
      if (selectedBusiness) {
        setPhoneNumber(selectedBusiness.phone);
      }
    }
  }, [orderStatusModalData]);
  useEffect(() => {
    updatePhoneNumber();
  }, [updatePhoneNumber]);
  if (!orderStatusModalData) return null;

  const status = orderStatusModalData?.status;
  const businessName = orderStatusModalData?.business_name;
  const orderType = orderStatusModalData?.order_type;
  const orderReadyTime = calculateOrderReadyTime(orderStatusModalData, false);
  const deliveryTrackingURL = orderStatusModalData?.delivery_tracking_url;
  const delay = orderStatusModalData?.delay;
  const statusMessage = orderStatusModalData?.status_change_message
    ? orderStatusModalData?.status_change_message
    : ' Unfortunately, we cannot fulfill your order at this time. Your order will be refunded.';

  return (
    <Dialog
      open={orderStatusModalData}
      classes={classes}
      onBackdropClick={() => setOrderStatusModalData(null)}
      scroll='body'
      maxWidth={'md'}
      fullWidth={true}
    >
      <Wrapper>
        <StatusCardWrapper>
          <TitleWrapper>
            <Title id='OrderStatus-Title'>Order Status</Title>
            <div>
              <CloseButton
                id='OrderStatusModal-SetOrderStatus'
                onClick={() => setOrderStatusModalData(null)}
              />
            </div>
          </TitleWrapper>
          <OrderStatusBody
            orderType={orderType}
            status={status}
            businessName={businessName}
            orderReadyTime={orderReadyTime}
            deliveryTrackingURL={deliveryTrackingURL}
            delay={delay}
            displayComplete={displayComplete}
            statusMessage={statusMessage}
          />
        </StatusCardWrapper>
        <DetailsWrapper>
          {status !== ORDER_REFUNDED && status !== ORDER_REJECTED && (
            <StepsSummary
              orderStatusModalData={orderStatusModalData}
              orderReadyTime={orderReadyTime}
              phoneNumber={phoneNumber}
              displayComplete={displayComplete}
            />
          )}
          <DetailWrapper>
            <DetailTitle id='OrderStatusModal-OrderSummary'>
              Your Items
            </DetailTitle>
            <ProductPreview
              products={orderStatusModalData?.flatProducts}
              isForPreview
            />
          </DetailWrapper>
          {orderStatusModalData?.special_instruction && (
            <DetailWrapper>
              <DetailTitle id='OrderStatusModal-OrderSummary'>
                Order Instructions
              </DetailTitle>
              <NoteWraper>
                <NoteText>{orderStatusModalData?.special_instruction}</NoteText>
              </NoteWraper>
              <Line size='2' />
            </DetailWrapper>
          )}
          <DetailWrapper>
            <ReceiptWrapper>
              <Receipt
                id='OrderStatusModal-Receipt'
                orderSummary={{
                  ...orderStatusModalData?.order_summary,
                  currency: orderStatusModalData?.currency
                    ? orderStatusModalData?.currency
                    : currency,
                  orderMethodType: orderStatusModalData?.order_type,
                  serviceFees: orderStatusModalData?.service_fees,
                }}
                is_tip_enabled={is_tip_enabled}
              />
            </ReceiptWrapper>
          </DetailWrapper>
          {orderStatusModalData?.order_summary?.total > 0 ? (
            <DetailWrapper>
              <DetailTitle id='OrderStatusModal-PaymentMethod'>
                Payment Method
              </DetailTitle>
              <PaymentMethod
                paymentMethod={orderStatusModalData?.payments[0]}
              />
            </DetailWrapper>
          ) : null}
        </DetailsWrapper>
      </Wrapper>
    </Dialog>
  );
};

export default withStyles(styles)(memo(OrderStatusModal2));
